import {AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild} from '@angular/core';
import { Chart } from 'angular-highcharts';
import {TransactionService} from '../../../services/transaction.service';
import {DisplayFieldPipe} from '../../pipes/displayField.pipe';
import * as _ from 'lodash';
import {Router} from '@angular/router';
import * as moment from 'moment-timezone';
import {HighchartsService} from '../../../services/highcharts.service';
import {DatesService} from '../../../services/dates.service';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';

@Component({
    selector: 'app-transactions-trading-partner',
    templateUrl: './transactions-trading-partner.component.html',
    styleUrls: ['./transactions-trading-partner.component.scss'],
    providers: [HighchartsService, DatesService]
})
export class TransactionsTradingPartnerComponent implements AfterViewInit, OnDestroy {

    @Input() chartOnly = false;
    @Input() height = '500';
    @Input() title = '';
    @Input() showEdiTypeSerie = false;
    @Input() preloadData: any = null;

    private preloadSubscription: Subscription;
    private seriesSubscription: Subscription;
    private detailsSubscription: Subscription;

    companies: Array<any> = [];
    vendors: Array<any> = [];
    customers: Array<any> = [];
    drilldownLevel = 0;

    aggrigatedBy: { [key: number]: number; } = {};

    minDate:   Date = new Date(2000, 1, 1);
    startYear: Date = new Date(2010, 1, 1);

    filters: any = {
        CO1_ID: [],
        ED1_MODIFIED_ON_FROM: null,
        ED1_MODIFIED_ON_TO: null
    };

    chart: Chart;

    smallPieChartData: any = {};
    smallPieChartIndex: number = null;
    needsToBeRedraw = false;

    constructor(
        private  transactionService: TransactionService,
        private highchartsService: HighchartsService,
        private datesService: DatesService,
        private router: Router,
        private appTitle: Title
    ) {
        this.aggrigatedBy[1] = HighchartsService.DRILLDOWN_YEAR;
        this.aggrigatedBy[2] = HighchartsService.DRILLDOWN_MONTH;
        this.aggrigatedBy[3] = HighchartsService.DRILLDOWN_DAY;
    }

    ngAfterViewInit() {
        if (!this.chartOnly) {
            this.appTitle.setTitle('EDI - Transactions');
        }
        if (this.preloadData === null) {
            this.preloadSubscription = this.transactionService.preloadData().subscribe(res => {
                this.handlePreloadData(res);
            }, () => {
                // failed to preload data
            });
        } else {
            this.handlePreloadData(this.preloadData);
        }
    }

    ngOnDestroy() {
        if (this.preloadSubscription) {
            this.preloadSubscription.unsubscribe();
        }
        if (this.seriesSubscription) {
            this.seriesSubscription.unsubscribe();
        }
        if (this.detailsSubscription) {
            this.detailsSubscription.unsubscribe();
        }
    }

    handlePreloadData( res ) {
        this.companies = res.companies;

        this.customers = res.customers.map( customer => {
            return {
                value:  customer.CO1_ID + '_' + customer.CU1_ID,
                CU1_NAME: customer.CU1_NAME
            }
        });

        this.vendors = res.vendors.map( vendor => {
            return {
                value:  vendor.CO1_ID + '_' + vendor.VD1_ID,
                VD1_NAME: vendor.VD1_NAME
            }
        });

        const me = this;
        this.chart = new Chart({
            chart: {
                type: 'pie',
                events: {
                    drillup: function(e) {
                        me.drilldownLevel --;

                        if (me.smallPieChartIndex) {
                            me.chart.removeSerie(me.smallPieChartIndex);
                            me.smallPieChartIndex = null;
                        }

                        me.needsToBeRedraw = true;
                    },

                    redraw: function() {

                        if (me.needsToBeRedraw) {
                            me.needsToBeRedraw = false;
                            if (me.drilldownLevel > 1) {
                                const data = me.smallPieChartData[me.drilldownLevel];
                                me.updateSmallPieChart(this, data.name, data.drilldown);
                            }
                        }
                    },
                    drilldown: function (e) {
                        if (!e.seriesOptions) {
                            const chart = this;
                            const serie = me.highchartsService.getSerieByPoint( me.chart, e.point );

                            if (serie) {
                                if ( me.drilldownLevel < 1) {
                                    chart.addSeriesAsDrilldown(e.point, {
                                        type: serie.drilldownType,
                                        name: serie.name,
                                        data: serie.drilldown,
                                        innerSize: '40%'
                                    });

                                    me.drilldownLevel ++;
                                } else {
                                    const params = {
                                        type:         'bar',
                                        CU1_ID:       serie.CU1_ID,
                                        VD1_ID:       serie.VD1_ID,
                                        CO1_ID:       serie.CO1_ID,
                                        groupBy:      serie.CU1_ID ? 'CU1_ID' : 'VD1_ID',
                                        aggrigatedBy: me.aggrigatedBy[me.drilldownLevel]
                                    };

                                    if ( me.drilldownLevel > 1 ) {
                                        const startDate  = moment.tz(new Date(e.point.x), 'UTC');
                                        let endDate = new Date( Date.UTC(startDate.year(), 11, 31) );
                                        if ( me.aggrigatedBy[me.drilldownLevel] == HighchartsService.DRILLDOWN_MONTH ) {
                                            endDate = new Date( Date.UTC(startDate.year(), 11, 31) );
                                        } else if ( me.aggrigatedBy[me.drilldownLevel] == HighchartsService.DRILLDOWN_DAY ) {
                                            endDate = new Date( Date.UTC(startDate.year(), startDate.month(), startDate.daysInMonth()) );
                                        }

                                        params['ED1_MODIFIED_ON_FROM'] = me.datesService.format(
                                            startDate.toString(), '00:00:00', 'UTC'
                                        );
                                        params['ED1_MODIFIED_ON_TO']   = me.datesService.format(
                                            moment(endDate).tz('UTC').toString(), '23:59:59', 'UTC'
                                        );

                                    } else {
                                        if (me.filters['ED1_MODIFIED_ON_FROM']) {
                                            params['ED1_MODIFIED_ON_FROM'] = me.datesService.format(me.filters['ED1_MODIFIED_ON_FROM']);
                                        }
                                        if (me.filters['ED1_MODIFIED_ON_TO']) {
                                            params['ED1_MODIFIED_ON_TO'] = me.datesService.format(
                                                me.filters['ED1_MODIFIED_ON_TO'], '23:59:59'
                                            );
                                        }
                                    }

                                    const series = [];
                                    series.push(params);
                                    series.push({
                                        type:    'pie',
                                        CU1_ID:  serie.CU1_ID,
                                        VD1_ID:  serie.VD1_ID,
                                        CO1_ID:  serie.CO1_ID,
                                        groupBy: 'ED1_TYPE',
                                        ED1_MODIFIED_ON_FROM: params['ED1_MODIFIED_ON_FROM'],
                                        ED1_MODIFIED_ON_TO:   params['ED1_MODIFIED_ON_TO']
                                    });

                                    // Show the loading label
                                    chart.showLoading('Loading ...');
                                    me.seriesSubscription = me.transactionService.getSeries(series).subscribe( data => {
                                        chart.hideLoading();
                                        if (me.smallPieChartIndex) {
                                            me.chart.removeSerie(me.smallPieChartIndex);
                                            me.smallPieChartIndex = null;
                                        }
                                        data.serie.forEach( serieData => {
                                            if (serieData.type == 'bar') {

                                                chart.addSeriesAsDrilldown(e.point, {
                                                    type: serieData.type,
                                                    name: serie.name,
                                                    data: serieData.data[0].data.map( item => {
                                                        const result = {};
                                                        result['name']   = serie.name;
                                                        result['type']   = 'bar';
                                                        result['x']      = item[0];
                                                        result['y']      = item[1];
                                                        result['CU1_ID'] = serie.CU1_ID;
                                                        result['VD1_ID'] = serie.VD1_ID;
                                                        result['CO1_ID'] = serie.CO1_ID;

                                                        if ( me.aggrigatedBy[me.drilldownLevel] > HighchartsService.DRILLDOWN_DAY) {
                                                            result['drilldown'] = [];
                                                            result['drilldownType']  = 'column';
                                                        }
                                                        return result;
                                                    }),
                                                    tooltip: {
                                                        headerFormat: '<span style=\'font-size: 10px\'>{point.x:%Y, %b %d}</span><br/>'
                                                    }
                                                });

                                            } else if (serieData.type == 'pie') {

                                                const drilldown = serieData.data.map( item => {
                                                    const result = {};
                                                    result['name']   = item.ED1_TYPE;
                                                    result['type']   = 'pie';
                                                    result['y']      = item.data[0][0];

                                                    return result;
                                                });

                                                me.drilldownLevel ++;
                                                me.updateSmallPieChart(chart, serie.name, drilldown);
                                            }
                                        });
                                    });
                                }
                            }
                        }
                    }
                }
            },

            xAxis: {
                title: {
                    text: 'Timeline'
                },
                type: 'datetime',
            },

            yAxis: {
                min: 0,
                title: {
                    text: 'Number of transactions'
                }
            },

            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: true,
                        padding: 1
                    }
                }
            },
            title: {
                text: this.title
            },
            tooltip: {
                pointFormat: 'Value: {point.y:,.0f}'
            }
        });
        this.onSubmit();
    }

    updateSmallPieChart(chart: any, name: string, drilldown: Array<any>) {
        const res = chart.addSeries({
            type: 'pie',
            name: name,
            data: drilldown,
            size: '30%',
            center: ['20%', '18%']
        });

        this.smallPieChartIndex = res['index'];
        this.smallPieChartData[this.drilldownLevel] = {
            name:      name,
            drilldown: drilldown
        };
    }

    loadChartData(params: any = null) {
        this.detailsSubscription = this.transactionService.getTradingPartnersReport(params).subscribe(result => {

            this.drilldownLevel = 0;

            this.highchartsService.destroyDrillDown( this.chart );
            this.highchartsService.removeAllSeries( this.chart );

            const me = this;
            const displayFieldPipe = new DisplayFieldPipe();
            const serieData = [];

            result.data.forEach(function (item) {
                const name = item.name ? item.name :
                    displayFieldPipe.transform(item.CO1_ID, 'CO1_ID', 'CO1_NAME', me.companies);

                const drilldowns = [];
                item.drilldown.forEach(drilldown => {
                    let ddName = '';
                    if ( parseInt(drilldown.VD1_ID, 10) ) {
                        ddName += displayFieldPipe.transform(
                            (item.CO1_ID + '_' + drilldown.VD1_ID), 'value', 'VD1_NAME', me.vendors, 'No Data'
                        );
                        if ( !/^Vendor/.test(ddName)) {
                            ddName = 'Vendor: ' + ddName;
                        }
                    } else {
                        ddName = displayFieldPipe.transform(
                            (item.CO1_ID + '_' + drilldown.CU1_ID), 'value', 'CU1_NAME', me.customers, 'No Data'
                        );
                        if ( !/^Customer/.test(ddName)) {
                            ddName = 'Customer: ' + ddName;
                        }
                    }

                    drilldowns.push({
                        name: ddName,
                        y: parseInt(drilldown.total, 10),
                        drilldownType: 'column',
                        drilldown: [],
                        CO1_ID: item.CO1_ID,
                        CU1_ID: drilldown.CU1_ID,
                        VD1_ID: drilldown.VD1_ID
                    });
                });

                serieData.push({
                    name: name ? name : 'Undefined Company',
                    y: parseInt(item.totals, 10),
                    drilldownType: 'pie',
                    drilldown: drilldowns
                });
            });

            me.chart.addSerie({
                data: serieData,
                dataLabels: {
                    padding: 5
                }
            });

            if (serieData.length == 1) {
                this.chart.ref.series[0].data[0]['firePointEvent']('click');
                this.chart.ref['drillUpButton'] = this.chart.ref['drillUpButton'].destroy();
            }
        });
    }

    onSubmit() {

        const filters = _.clone(this.filters);

        filters['CO1_ID[]'] = filters['CO1_ID'];
        delete filters['CO1_ID'];

        if (filters['ED1_MODIFIED_ON_FROM']) {
            filters['ED1_MODIFIED_ON_FROM'] = this.datesService.format(filters['ED1_MODIFIED_ON_FROM']);
        }
        if (filters['ED1_MODIFIED_ON_TO']) {
            filters['ED1_MODIFIED_ON_TO'] = this.datesService.format(filters['ED1_MODIFIED_ON_TO'], '23:59:59');
        }

        this.loadChartData(filters);
    }

    onCancel(): void {
        this.router.navigate(['/transactions']);
    }

}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/transactions/transactions-trading-partner/transactions-trading-partner.component.ts